import { c as _c } from "react/compiler-runtime";
import type { Icon } from '@onefootprint/icons';
import { IcoChevronDown16, IcoChevronRight16, IcoCloseSmall16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isTemplateBackedAgentId } from '../../data/agent-templates';
import type { AskPercyTemplate } from '../../data/templates';
export type ActionPillConfig = {
  kind: 'templates';
  id: string;
  label: string;
  icon: Icon;
  templates: AskPercyTemplate[];
  onSelect: (template: AskPercyTemplate) => void;
} | {
  kind: 'action';
  id: string;
  label: string;
  icon: Icon;
  prompt: string;
  onSelect: (prompt: string) => void;
};
export type ActionPillsProps = {
  pills: ActionPillConfig[];
  /** `row`: the landing's centred filled chips. `list`: the docked panel's ghost rows. */
  layout?: 'row' | 'list';
  onPreviewPromptChange?: (prompt: string | null) => void;
  disabled?: boolean;
  /** Opens a templates popover on mount, so a snapshot can cover it (stories). */
  defaultOpenPillId?: string;
};
const ActionPills = t0 => {
  const $ = _c(31);
  const {
    pills,
    layout: t1,
    onPreviewPromptChange,
    disabled,
    defaultOpenPillId
  } = t0;
  const layout = t1 === undefined ? "row" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "actionPills"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("ask-percy", t2);
  const [openPillId, setOpenPillId] = useState(defaultOpenPillId ?? null);
  const [hoveredTemplateId, setHoveredTemplateId] = useState(null);
  const popoverRef = useRef(null);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const triggerRefs = useRef(t3);
  let t4;
  if ($[2] !== openPillId) {
    t4 = p => p.id === openPillId && p.kind === "templates";
    $[2] = openPillId;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const openPill = pills.find(t4);
  let t5;
  let t6;
  if ($[4] !== onPreviewPromptChange || $[5] !== openPillId) {
    t5 = () => {
      if (!openPillId) {
        return;
      }
      const close = () => {
        setOpenPillId(null);
        setHoveredTemplateId(null);
        onPreviewPromptChange?.(null);
      };
      const onMouseDown = e => {
        const target = e.target as Node;
        if (popoverRef.current?.contains(target)) {
          return;
        }
        const trigger = triggerRefs.current.get(openPillId);
        if (trigger?.contains(target)) {
          return;
        }
        close();
      };
      const onKeyDown = e_0 => {
        if (e_0.key === "Escape") {
          close();
        }
      };
      document.addEventListener("mousedown", onMouseDown);
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.removeEventListener("mousedown", onMouseDown);
        document.removeEventListener("keydown", onKeyDown);
      };
    };
    t6 = [openPillId, onPreviewPromptChange];
    $[4] = onPreviewPromptChange;
    $[5] = openPillId;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] !== disabled || $[9] !== onPreviewPromptChange) {
    t7 = pill => {
      if (disabled) {
        return;
      }
      if (pill.kind === "action") {
        pill.onSelect(pill.prompt);
        onPreviewPromptChange?.(null);
        return;
      }
      setOpenPillId(prev => prev === pill.id ? null : pill.id);
      setHoveredTemplateId(null);
    };
    $[8] = disabled;
    $[9] = onPreviewPromptChange;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handlePillClick = t7;
  let t8;
  if ($[11] !== onPreviewPromptChange) {
    t8 = template => {
      setHoveredTemplateId(template?.id ?? null);
      onPreviewPromptChange?.(template && !isTemplateBackedAgentId(template.id) ? template.prompt : null);
    };
    $[11] = onPreviewPromptChange;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleTemplateHover = t8;
  const t9 = layout === "list" ? "w-full" : "-mt-2 w-full md:w-auto max-w-full";
  let t10;
  if ($[13] !== t9) {
    t10 = cx("relative", t9);
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = layout === "list" ? "flex flex-col items-start gap-0.5" : "flex flex-col md:flex-row md:justify-center md:items-center gap-1.5 md:gap-2 md:flex-wrap";
  let t12;
  if ($[15] !== t11) {
    t12 = cx(t11);
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== disabled || $[18] !== handlePillClick || $[19] !== layout || $[20] !== onPreviewPromptChange || $[21] !== openPillId || $[22] !== pills) {
    t13 = pills.map(pill_0 => {
      const PIcon = pill_0.icon;
      return <button key={pill_0.id} ref={el => {
        if (el) {
          triggerRefs.current.set(pill_0.id, el);
        } else {
          triggerRefs.current.delete(pill_0.id);
        }
      }} type="button" disabled={disabled} onClick={() => handlePillClick(pill_0)} onMouseEnter={() => !disabled && pill_0.kind === "action" && onPreviewPromptChange?.(pill_0.prompt)} onMouseLeave={() => !disabled && pill_0.kind === "action" && onPreviewPromptChange?.(null)} className={cx("max-w-full text-label-3 transition-colors", {
        "flex items-center gap-2 h-7 px-2 rounded-default": layout === "list",
        "bg-senary/70 text-primary": layout === "list" && openPillId === pill_0.id,
        "text-tertiary hover:bg-hover-control hover:text-primary": layout === "list" && openPillId !== pill_0.id,
        "flex items-center justify-center gap-1.5 h-9 px-3 rounded-full text-primary": layout === "row",
        "bg-senary/60": layout === "row" && openPillId === pill_0.id,
        "bg-secondary hover:bg-hover-control": layout === "row" && openPillId !== pill_0.id,
        "cursor-default": disabled,
        "cursor-pointer": !disabled
      })}><PIcon className="shrink-0" /><span className="truncate">{pill_0.label}</span>{pill_0.kind === "templates" && <IcoChevronDown16 className={cx("shrink-0 transition-transform", {
          "rotate-180": openPillId === pill_0.id,
          "-ml-1": layout === "list",
          "ml-0.5": layout === "row"
        })} />}</button>;
    });
    $[17] = disabled;
    $[18] = handlePillClick;
    $[19] = layout;
    $[20] = onPreviewPromptChange;
    $[21] = openPillId;
    $[22] = pills;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = <div className={t12}>{t13}</div>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = openPill && <div className={cx("absolute bottom-full mb-2 w-[420px] max-w-[calc(100vw-32px)] animate-slideUp z-10", layout === "list" ? "left-0" : "left-1/2 -translate-x-1/2")}><div ref={popoverRef} className="bg-primary border border-tertiary rounded-[8px] shadow-md pt-2 px-2 overflow-y-auto max-h-[min(24rem,calc(100vh-8rem))]" onMouseLeave={() => handleTemplateHover(null)}><div className="flex items-center justify-between px-1 pb-2"><div className="flex items-center gap-1.5 text-caption-1 text-tertiary"><openPill.icon color="tertiary" />{openPill.label}</div><button type="button" aria-label={t(_temp)} onClick={() => {
          setOpenPillId(null);
          handleTemplateHover(null);
        }} className="w-6 h-6 rounded-(--icon-button-border-radius-tiny) flex items-center justify-center cursor-pointer hover:bg-hover-control"><IcoCloseSmall16 color="tertiary" /></button></div><div className="max-h-[144px] overflow-y-auto pb-2 -mr-2 pr-2">{openPill.templates.map(t_0 => {
          const TIcon = t_0.icon;
          const isHovered = hoveredTemplateId === t_0.id;
          return <button key={t_0.id} type="button" onClick={() => {
            openPill.onSelect(t_0);
            setOpenPillId(null);
            handleTemplateHover(null);
          }} onMouseEnter={() => handleTemplateHover(t_0)} onMouseLeave={() => handleTemplateHover(null)} className={`w-full h-8 flex items-center justify-between px-2 rounded-md cursor-pointer transition-colors ${isHovered ? "bg-hover" : "hover:bg-hover"}`}><span className="flex items-center gap-2 text-label-3 text-primary"><TIcon color="primary" />{t_0.title}</span><IcoChevronRight16 color="tertiary" /></button>;
        })}</div></div></div>;
  let t16;
  if ($[27] !== t10 || $[28] !== t14 || $[29] !== t15) {
    t16 = <div className={t10}>{t14}{t15}</div>;
    $[27] = t10;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  return t16;
};
export default ActionPills;
function _temp($) {
  return $.closeTemplates;
}